import React, { useEffect, useMemo, useRef, useState } from 'react';
import TargetBadge from './TargetBadge';
import { normalizePercentualValue } from '../utils/parseVolume';

const COLORS = { greenDark: '#267100', greenLight: '#9bca3cc0', redDark: '#BC0000', redLight: '#F37F61', silver: '#9DA2B6' };
const RECENT_CROSS_MS = 1200;
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

function formatUnidade(value, unit, options = {}) {
  if (value === null || value === undefined) return '-';

  const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
  const numValue = Number(value);

  if (isNaN(numValue)) return '-';

  const formattedValue = numValue.toLocaleString('pt-BR', {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  const normalizedUnit = String(unit || '').toLowerCase().trim();

  switch (normalizedUnit) {
    case 'moeda':
      return `R$ ${formattedValue}`;

    case 'toneladas':
      return `${formattedValue} t`;

    case 'percentual': {
      const percentValue = normalizePercentualValue(numValue);

      const isInteger = Math.abs(percentValue - Math.round(percentValue)) < 0.0001;

      return `${percentValue.toLocaleString('pt-BR', {
        minimumFractionDigits: isInteger ? 0 : 2,
        maximumFractionDigits: isInteger ? 0 : 2,
      })}%`;
    }

    case 'unidade': {
      const isInteger = Math.abs(numValue - Math.round(numValue)) < 0.0001;

      return numValue.toLocaleString('pt-BR', {
        minimumFractionDigits: isInteger ? 0 : 2,
        maximumFractionDigits: isInteger ? 0 : 2,
      });
    }

    default:
      return formattedValue;
  }
}

function makeAnchoredMapper(thresholds, vMaxFallback, uiAnchors, descending = false) {
  const pts = [];
  const { m100, m300, m500, m700, bonus } = thresholds || {};
  const mapV = (v) => v;
  const hasM700Asc  = Number.isFinite(m700) && m700 > 0;
  const hasM700Desc = Number.isFinite(m700);
  const hasM700 = descending ? hasM700Desc : hasM700Asc;
  const hasBonus = Number.isFinite(bonus) && bonus > 0;

  const cand = [];
  if (Number.isFinite(m100)) cand.push(mapV(m100));
  if (Number.isFinite(m300)) cand.push(mapV(m300));
  if (Number.isFinite(m500)) cand.push(mapV(m500));
  const endCand = [
    hasM700 ? mapV(m700) : -Infinity,
    hasBonus ? mapV(bonus) : -Infinity,
    mapV(vMaxFallback || 1)
  ];
  cand.push(...endCand.filter((v) => Number.isFinite(v)));
  let minV = cand.length ? Math.min(...cand) : 0;
  let maxV = cand.length ? Math.max(...cand) : 1;
  if (descending) {
    minV = Math.min(minV, -1);
    maxV = Math.max(maxV, 0);
  }
  const margin = Math.max(1e-6, Math.abs(maxV - minV) * 0.02);
  const baseV = minV - margin;

  pts.push({ v: baseV, p: 0 });
  if (Number.isFinite(m100)) pts.push({ v: mapV(m100), p: uiAnchors.M100 });
  if (Number.isFinite(m300)) pts.push({ v: mapV(m300), p: uiAnchors.M300 });
  if (Number.isFinite(m500)) pts.push({ v: mapV(m500), p: uiAnchors.M500 });
  let vEnd;
  if (hasM700) {
    vEnd = descending ? mapV(m700) : mapV(m700);
  } else {
    const baseEnd = mapV(vMaxFallback || 1);
    const bonusEnd = hasBonus ? mapV(bonus) : -Infinity;
    vEnd = Math.max(
      Number.isFinite(baseEnd) ? baseEnd : -Infinity,
      Number.isFinite(bonusEnd) ? bonusEnd : -Infinity,
      maxV
    );
  }
  pts.push({ v: vEnd, p: uiAnchors.bonus });
  pts.sort((a, b) => a.v - b.v);

  return (v) => {
    const vv = mapV(v);
    if (!Number.isFinite(vv)) return 0;
    if (vv <= pts[0].v) return 0;
    if (vv >= pts[pts.length - 1].v) return 100;
    let i = 1;
    while (i < pts.length && vv > pts[i].v) i++;
    const a = pts[i - 1], b = pts[i];
    const t = (vv - a.v) / Math.max(1e-6, b.v - a.v);
    return (a.p + (b.p - a.p) * t) * 100;
  };
}

export default function ProgressRail({
  thresholds,
  volume,
  reached,
  isMobile,
  isOpen,
  projectedVolume,
  showProjected,
  descending = false,
  startOffsetPx = 0,
  zeroBallShiftPercent = 3,
  volumeLabel = 'Volume Real Ajustado',
  projectedLabel = 'Volume Real Projetado',
  unit = 'toneladas'
}) {
  const { useMemo, useEffect, useState, useRef, useCallback } = React;
  const uiAnchors = useMemo(() => {
    return descending
      ? (isMobile
          ? { M100: 0.18, M300: 0.46, M500: 0.74, bonus: 1.00 }
          : { M100: 0.22, M300: 0.50, M500: 0.78, bonus: 1.00 })
      : (isMobile
          ? { M100: 0.28, M300: 0.52, M500: 0.76, bonus: 1.00 }
          : { M100: 0.30, M300: 0.54, M500: 0.78, bonus: 1.00 });
  }, [isMobile, descending]);

  const pMapper = useMemo(() => {
    return makeAnchoredMapper(
      thresholds,
      Math.max(thresholds?.m500 || 0, projectedVolume || 0, volume || 0, 1),
      uiAnchors,
      descending
    );
  }, [thresholds, volume, projectedVolume, uiAnchors, descending]);


  const hasM100 = Number.isFinite(thresholds?.m100);
  const hasM300 = Number.isFinite(thresholds?.m300);
  const hasM500 = Number.isFinite(thresholds?.m500);
  const pM100 = hasM100 ? pMapper(thresholds.m100) : null;
  const pM300 = hasM300 ? pMapper(thresholds.m300) : null;
  const pM500 = hasM500 ? pMapper(thresholds.m500) : null;
  const hasM700 = descending
    ? Number.isFinite(thresholds?.m700)
    : (Number.isFinite(thresholds?.m700) && thresholds.m700 > 0);
  const hasBonus = Number.isFinite(thresholds?.bonus) && thresholds.bonus > 0;
  const pM700 = hasM700
    ? (descending ? (uiAnchors?.bonus ?? 1) * 100 : pMapper(thresholds.m700))
    : null;
  const pCurrentMapped = pMapper(volume ?? 0);
  const pCurrentForAnim = useMemo(() => {
    const m100 = thresholds?.m100;
    if (!descending && Number.isFinite(volume) && Number.isFinite(m100) && m100 > 0) {
      if (volume > 0 && volume < m100) {
        const anchorP = (uiAnchors?.M100 ?? 0.3) * 100;
        const ratio = clamp(volume / m100, 0, 1);
        const pRatio = anchorP * ratio;
        const SNAP_WIN = isMobile ? 0.6 : 0.4;
        if ((anchorP - pRatio) <= SNAP_WIN && ratio >= 0.995) return anchorP;
        return pRatio;
      }
    }
    return pCurrentMapped;
  }, [descending, volume, thresholds?.m100, uiAnchors, pCurrentMapped, isMobile]);
  const pProjected = Number.isFinite(projectedVolume) ? pMapper(projectedVolume) : null;
  const pProjectedForAnim = useMemo(() => {
    const m100 = thresholds?.m100;
    if (!descending && Number.isFinite(projectedVolume) && Number.isFinite(m100) && m100 > 0) {
      if (projectedVolume > 0 && projectedVolume < m100) {
        const anchorP = (uiAnchors?.M100 ?? 0.3) * 100;
        const ratio = clamp(projectedVolume / m100, 0, 1);
        const pRatio = anchorP * ratio;
        const SNAP_WIN = isMobile ? 0.6 : 0.4;
        if ((anchorP - pRatio) <= SNAP_WIN && ratio >= 0.995) return anchorP;
        return pRatio;
      }
    }
    return pProjected;
  }, [descending, projectedVolume, thresholds?.m100, uiAnchors, pProjected, isMobile]);

  const [pAnim, setPAnim] = useState(0);
  const [hit, setHit] = useState({ M100: false, M300: false, M500: false, M700: false });
  const rafRef = useRef(null);
  const [realDone, setRealDone] = useState(false);
  const [projAnimP, setProjAnimP] = useState(null);
  const projRafRef = useRef(null);

  const [ballHovered, setBallHovered] = useState(false);
  const [ballExpanded, setBallExpanded] = useState(false);

  const railRef = useRef(null);
  const innerWrapperRef = useRef(null);
  const cardRefs = useRef([]);
  const circleRefs = useRef([]);
  const [coverages, setCoverages] = useState([]);
  const prevPAnimRef = useRef(0);
  const crossTimesRef = useRef({});

  const EPS_NEG = 1e-6;
  const nearExtremeNegVol = descending && Number.isFinite(volume) && (volume <= (-1 + EPS_NEG));
  const computeBallPct = useCallback((p) => {
    const rail = railRef.current;
    const inner = innerWrapperRef.current;
    if (!rail || !inner) {
      const zeroShift = (!Number.isFinite(thresholds?.m100) || (thresholds?.m100 ?? 0) <= 0)
        ? (isMobile ? 0.6 : 0.8)
        : zeroBallShiftPercent;
      return p <= 0 ? zeroShift : Math.min(100, p);
    }
    const railRect = rail.getBoundingClientRect();
    const innerRect = inner.getBoundingClientRect();
    const styles = window.getComputedStyle(inner);
    const padLeft = parseFloat(styles.paddingLeft || '0') || 0;
    const padRight = parseFloat(styles.paddingRight || '0') || 0;
    const contentLeft = innerRect.left + padLeft;
    const contentWidth = Math.max(0, innerRect.width - padLeft - padRight);
    const offsetPct = ((contentLeft - railRect.left) / Math.max(1, railRect.width)) * 100;
    const scale = contentWidth / Math.max(1, railRect.width);
    const isLeftEdge = (p <= 0) || nearExtremeNegVol;
    if (isLeftEdge) {
      const isZeroMeta = (!Number.isFinite(thresholds?.m100) || (thresholds?.m100 ?? 0) <= 0);
      const extraShift = descending ? (isMobile ? 3 : 3) : 0;
      const zeroShift = (isZeroMeta
        ? (isMobile ? 0.25 : 0.3)
        : zeroBallShiftPercent) + extraShift;
      const innerPct = Math.min(100, zeroShift) * scale;
      const extraLeftPx = isZeroMeta ? (padLeft + 2) : 0;
      const extraLeftPct = (extraLeftPx / Math.max(1, railRect.width)) * 100;
      return clamp(offsetPct - extraLeftPct + innerPct, 0, 100);
    }
    const rightNudgePct = (descending && p >= 100) ? (isMobile ? 0.4 : 0.9) : 0;
    return clamp(offsetPct + (Math.min(100, p) + rightNudgePct) * scale, 0, 100);
  }, [descending, isMobile, thresholds?.m100, zeroBallShiftPercent, nearExtremeNegVol]);

  const computeTargetPct = useCallback((p) => {
    const rail = railRef.current;
    const inner = innerWrapperRef.current;
    if (!rail || !inner) return Math.min(100, Math.max(0, p));
    const railRect = rail.getBoundingClientRect();
    const innerRect = inner.getBoundingClientRect();
    const styles = window.getComputedStyle(inner);
    const padLeft = parseFloat(styles.paddingLeft || '0') || 0;
    const padRight = parseFloat(styles.paddingRight || '0') || 0;
    const contentLeft = innerRect.left + padLeft;
    const contentWidth = Math.max(0, innerRect.width - padLeft - padRight);
    const offsetPct = ((contentLeft - railRect.left) / Math.max(1, railRect.width)) * 100;
    const scale = contentWidth / Math.max(1, railRect.width);
    return clamp(offsetPct + Math.min(100, Math.max(0, p)) * scale, 0, 100);
  }, []);

  useEffect(() => {
  setPAnim(0);
  setRealDone(false);
  setProjAnimP(null);
  if (projRafRef.current) cancelAnimationFrame(projRafRef.current);
    setHit({ M100: false, M300: false, M500: false });
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  const target = Number.isFinite(pCurrentForAnim) ? clamp(pCurrentForAnim, 0, 100) : 0;
    const tentative = 600 + (target / 100) * 1800;
    const durationMs = Number.isFinite(tentative) ? clamp(tentative, 1000, 3000) : 1500;

    if (target <= 0) {
      setPAnim(0);
      setHit({ M100: false, M300: false, M500: false });
      return;
    }

    let start = 0;
    let frame = 0;

    const tick = (now) => {
      if (!start) start = now;
      const rawT = (now - start) / (durationMs || 1);
      const t = clamp(Number.isFinite(rawT) ? rawT : 0, 0, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      const curr = 0 + (target - 0) * eased;

      setPAnim(curr);
      setHit((prev) => {
        const next = { ...prev };
        if (!prev.M100 && curr >= pM100) next.M100 = true;
        if (!prev.M300 && curr >= pM300) next.M300 = true;
        if (!prev.M500 && curr >= pM500) next.M500 = true;
        if (Number.isFinite(pM700) && !prev.M700 && curr >= pM700) next.M700 = true;
        return (next.M100 !== prev.M100 || next.M300 !== prev.M300 || next.M500 !== prev.M500 || next.M700 !== prev.M700) ? next : prev;
      });

      if (t < 1) frame = requestAnimationFrame(tick);
      else {
        setPAnim(target);
        setHit((prev) => {
          const next = { ...prev };
          if (!prev.M100 && target >= pM100) next.M100 = true;
          if (!prev.M300 && target >= pM300) next.M300 = true;
          if (!prev.M500 && target >= pM500) next.M500 = true;
          if (Number.isFinite(pM700) && !prev.M700 && target >= pM700) next.M700 = true;
          return (next.M100 !== prev.M100 || next.M300 !== prev.M300 || next.M500 !== prev.M500 || next.M700 !== prev.M700) ? next : prev;
        });
        setRealDone(true);
      }
    };

    frame = requestAnimationFrame(tick);
    return () => frame && cancelAnimationFrame(frame);
  }, [pCurrentForAnim, pM100, pM300, pM500, pM700, isOpen])

  useEffect(() => {
    if (!realDone || !showProjected || !Number.isFinite(pProjectedForAnim)) {
      if (projRafRef.current) cancelAnimationFrame(projRafRef.current);
      setProjAnimP(null);
      return;
    }
    if (projRafRef.current) cancelAnimationFrame(projRafRef.current);
    const startP = clamp(Math.min(100, Math.max(0, pAnim)), 0, 100);
    const destP  = clamp(Math.min(100, Math.max(0, pProjectedForAnim)), 0, 100);
    let start = 0;
    const durationMs = 700;
    const tick = (now) => {
      if (!start) start = now;
      const rawT = (now - start) / (durationMs || 1);
      const t = clamp(Number.isFinite(rawT) ? rawT : 0, 0, 1);
      const eased = easeOutCubic(t);
      const curr = startP + (destP - startP) * eased;
      setProjAnimP(curr);
      if (t < 1) projRafRef.current = requestAnimationFrame(tick);
      else setProjAnimP(destP);
    };
    projRafRef.current = requestAnimationFrame(tick);
    return () => projRafRef.current && cancelAnimationFrame(projRafRef.current);
  }, [realDone, showProjected, pProjectedForAnim, pAnim]);


  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    const railRect = rail.getBoundingClientRect();
    const mappedBallPct = computeBallPct(Math.min(100, Math.max(0, pAnim ?? 0)));
    const progressX = railRect.left + (railRect.width * mappedBallPct / 100);
    const arr = targets.map((t, idx) => {
      const elCard = cardRefs.current[idx];
      const elCircle = circleRefs.current[idx];
      let card = 0, circle = 0;
      if (elCard) {
        const r = elCard.getBoundingClientRect();
        card = (progressX - r.left) / Math.max(1, r.width);
        card = Math.max(0, Math.min(1, card));
      }
      if (elCircle) {
        const r = elCircle.getBoundingClientRect();
        circle = (progressX - r.left) / Math.max(1, r.width);
        circle = Math.max(0, Math.min(1, circle));
      }
      return { card, circle };
    });
    setCoverages(arr);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pAnim]);

  const targets = [
    hasM100 ? { pos: pM100, label: 'M100', val: thresholds.m100, displayVal: formatUnidade(thresholds.m100, unit), key: 'M100' } : null,
    hasM300 ? { pos: pM300, label: 'M300', val: thresholds.m300, displayVal: formatUnidade(thresholds.m300, unit), key: 'M300' } : null,
    hasM500 ? { pos: pM500, label: 'M500', val: thresholds.m500, displayVal: formatUnidade(thresholds.m500, unit), key: 'M500' } : null,
    hasM700 ? { pos: pM700, label: 'Créditos', val: thresholds.m700, displayVal: null, key: 'M700', isBonus: true } : null
  ].filter(t => t && Number.isFinite(t.pos));

  useEffect(() => {
    const prev = prevPAnimRef.current ?? 0;
    const now = pAnim ?? 0;
    targets.forEach((t) => {
      const pos = t.pos ?? 0;
      if (prev < pos && now >= pos) {
        crossTimesRef.current[t.key] = Date.now();
      }
    });
    prevPAnimRef.current = now;
  }, [pAnim, targets]);

  const OVERLAP_WIN = isMobile ? 4 : 2.5;
  const isExtremeNeg = descending && Number.isFinite(volume) && volume <= -1 + 1e-12;
  const isYellowZone = Number.isFinite(pM500) && Number.isFinite(pAnim) && (pAnim >= pM500);
  const DARK_YELLOW = '#D9A100';
  const ballColor = isExtremeNeg
    ? COLORS.redDark
    : (isYellowZone ? DARK_YELLOW : (reached ? COLORS.greenDark : COLORS.redDark));
  const ballProjColor = '#1F61AB';

  const CLOSE_WIN = isMobile ? 12 : 12;
  const hasTwoBalls = !!(showProjected && Number.isFinite(pAnim) && Number.isFinite(projAnimP));
  const ballsClose = hasTwoBalls && Math.abs((projAnimP ?? 0) - (pAnim ?? 0)) <= CLOSE_WIN;
  const realIsLeft = hasTwoBalls ? ((pAnim ?? 0) <= (projAnimP ?? 0)) : null;
  const labelWidthPx = isMobile ? 120 : 160;
  const labelGapPx = 4;


  useEffect(() => {
    setBallExpanded(true);
    const id = setInterval(() => setBallExpanded((v) => !v), 1000);
    return () => clearInterval(id);
  }, []);

  const baseSizePx = isMobile ? 24 : 35;
  const shrinkPx   = isMobile ? 2 : 3;
  const scaleMin   = (baseSizePx - shrinkPx) / baseSizePx;
  const ballScale  = ballExpanded ? 1 : Math.max(0.85, scaleMin);
  const ballTransformStyle = { transform: `scale(${ballScale})`, transition: 'transform 900ms cubic-bezier(0.4,0,0.2,1)' };

  return (
    <div className="my-3">
      {/* keyframes locais para animações da borda e das listras internas */}
      <style>{`
        @keyframes borderFlow {
          0% { background-position: 0% 0%; }
          100% { background-position: 200% 0%; }
        }
        @keyframes stripeFlow {
          0% { background-position: 0 0; }
          100% { background-position: 40px 0; }
        }
      `}</style>
      <div className="relative h-[160px] w-[92%]">
        <div
          ref={railRef}
          className="absolute top-0 left-0 h-full"
          style={{ left: `${startOffsetPx}px`, width: `calc(100% - ${startOffsetPx}px)` }}
        >
        {targets.map((t, i) => {
          const key = t.key;
          const isBonus = key === 'BONUS' || t.isBonus === true;
          const targetPos = clamp(t.pos ?? 0, 0, 100);
          const currentAnim = clamp(pAnim ?? 0, 0, 100);
          const animReachedTarget = currentAnim >= (targetPos - 0.001);
          const hitNow = key === 'M100'
            ? (!!reached && animReachedTarget)
            : (isBonus ? (currentAnim >= 100) : !!hit[key]);

          const covCard = coverages[i]?.card ?? 0;
          const covCirc = coverages[i]?.circle ?? 0;

          const near = Math.abs((pAnim ?? 0) - (t.pos ?? 0)) <= OVERLAP_WIN;
          const crossedAt = crossTimesRef.current[key] || 0;
          const justCrossed = crossedAt && (Date.now() - crossedAt) <= RECENT_CROSS_MS;
          const active = near || justCrossed;

          return (
            <div
              key={key}
              style={{ left: `${computeTargetPct(Math.min(100, t.pos))}%` }}
              className="absolute -translate-x-1/2 z-[160]"
            >
              <TargetBadge
                label={t.label}
                value={t.val}
                displayValue={t.displayVal}
                isBonus={isBonus}
                hitNow={hitNow}
                active={active}
                coverageCard={covCard}
                coverageCircle={covCirc}
                colors={key === 'M100' && !reached ? { green: '#BC0000', greenLight: '#F37F61', silver: '#9DA2B6' } : undefined}
                setCardRef={(el) => { cardRefs.current[i] = el; }}
                setCircleRef={(el) => { circleRefs.current[i] = el; }}
              />
            </div>
          );
        })}
        <div
          className="relative z-[80] inset-x-2 top-[72px] md:top-20 rounded-full overflow-hidden"
          style={{
            padding: '2px',
            backgroundImage: 'linear-gradient(90deg, #9ACA3C, #267100, #9ACA3C)',
            backgroundSize: '200% 100%',
            animation: 'borderFlow 4s linear infinite'
          }}
        >
          <div ref={innerWrapperRef} className="relative rounded-full bg-white flex justify-left items-center py-2 px-2">
            <div
              className={`${reached ? 'bg-gradient-to-r to-[#9ACA3C] from-[#267100]' : 'bg-gradient-to-r to-[#F37F61] from-[#BC0000]'} rounded-l-full h-4 md:h-5 relative overflow-hidden`}
              style={{ width: `${Math.min(100, pAnim)}%` }}
            >
              <div
                className="absolute inset-0 opacity-20 pointer-events-none"
                style={{
                  backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.0) 0 10px, rgba(255,255,255,0.35) 10px 20px)',
                  backgroundSize: '40px 40px',
                  animation: 'stripeFlow 2.2s linear infinite'
                }}
              />
            </div>
            {Number.isFinite(pM500) && Number.isFinite(pAnim) && (pAnim >= pM500) && (
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full pointer-events-none overflow-hidden"
                style={{
                  left: `${Math.min(100, pM500)}%`,
                  width: `${Math.max(0,
                    Math.min(
                      Math.min(100, pAnim) - Math.min(100, pM500),
                      Number.isFinite(pM700) ? (Math.min(100, pM700) - Math.min(100, pM500)) : 100
                    )
                  )}%`,
                  height: isMobile ? '1rem' : '1.25rem',
                  backgroundImage: 'linear-gradient(90deg, #E3B400, #F5C115)',
                  opacity: 1,
                  zIndex: 3
                }}
              >
                <div
                  className="absolute inset-0 opacity-12 pointer-events-none"
                  style={{
                    backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.0) 0 10px, rgba(255,255,255,0.35) 10px 20px)',
                    backgroundSize: '40px 40px',
                    animation: 'stripeFlow 2.2s linear infinite'
                  }}
                />
              </div>
            )}
            {showProjected && Number.isFinite(pAnim) && Number.isFinite(projAnimP) && Math.abs(projAnimP - pAnim) > 0.25 && (
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full pointer-events-none overflow-hidden"
                style={{
                  left: `${Math.min(Math.max(0, Math.min(100, pAnim)), Math.max(0, Math.min(100, projAnimP)))}%`,
                  width: `${Math.abs(Math.max(0, Math.min(100, projAnimP)) - Math.max(0, Math.min(100, pAnim)))}%`,
                  height: isMobile ? '1rem' : '1.25rem',
                  backgroundImage: `linear-gradient(90deg, #2A6BB4, ${ballProjColor})`,
                  opacity: 1,
                  zIndex: 2
                }}
              >
                <div
                  className="absolute inset-0 opacity-16 pointer-events-none"
                  style={{
                    backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.0) 0 10px, rgba(255,255,255,0.35) 10px 20px)',
                    backgroundSize: '40px 40px',
                    animation: 'stripeFlow 2.2s linear infinite'
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div style={{ left: `${computeBallPct(pAnim)}%`, transform: 'translateX(-50%)' }} className="absolute top-[76px] md:top-[80px] flex flex-col items-center z-[165] transition-none">
          <div className="relative grid place-items-center">
            <div
              className="absolute rounded-full border-2 opacity-40 animate-ping pointer-events-none md:hidden"
              style={{ width: '24px', height: '24px', borderColor: ballColor, zIndex: 160, animationDuration: '2.6s' }}
            />
            <div
              className="absolute rounded-full border-2 opacity-30 animate-ping pointer-events-none md:hidden"
              style={{ width: '24px', height: '24px', borderColor: ballColor, zIndex: 160, animationDelay: '1.3s', animationDuration: '2.6s' }}
            />
            <div
              className="absolute hidden md:block rounded-full border-4 opacity-40 animate-ping pointer-events-none"
              style={{ width: '35px', height: '35px', borderColor: ballColor, zIndex: 160, animationDuration: '2.6s' }}
            />
            <div
              className="absolute hidden md:block rounded-full border-4 opacity-30 animate-ping pointer-events-none"
              style={{ width: '35px', height: '35px', borderColor: ballColor, zIndex: 160, animationDelay: '1.3s', animationDuration: '2.6s' }}
            />
            <div
              className={`relative  mt-0.5 z-[170] w-6 h-6 md:w-[35px] md:h-[35px] rounded-full border-2 md:border-4 transition-colors duration-150 pointer-events-auto ${ballHovered ? 'ring-[8px] ring-[#E9E9E9]/50' : 'hover:ring-[8px] hover:ring-[#E9E9E9]/50'}`}
              style={{ backgroundColor: ballColor, borderColor: ballColor, ...ballTransformStyle }}
              onMouseEnter={() => setBallHovered(true)}
              onMouseLeave={() => setBallHovered(false)}
            />
          </div>
          <div className="-mt-1 w-[2px] h-4 transition-colors duration-150 pointer-events-none" style={{ backgroundColor: ballColor }} />
          {ballsClose && hasTwoBalls ? (
            <div className="relative mt-[-2px]" style={{ width: 0 }}>
              <div
                style={{
                  position: 'absolute',
                  left: realIsLeft ? 'auto' : `${labelGapPx}px`,
                  right: realIsLeft ? `${labelGapPx}px` : 'auto',
                  width: `${labelWidthPx}px`,
                  textAlign: realIsLeft ? 'right' : 'left'
                }}
              >
                <div className="font-poppins text-[12px] md:text-[16px] font-semibold text-[#031843] whitespace-nowrap">
                  {Number.isFinite(volume) ? formatUnidade(volume, unit) : '-'}
                </div>
                <div className="font-poppins text-[9px] md:text-[10px] text-[#031843] -mt-1 whitespace-nowrap">
                  {volumeLabel}
                </div>
              </div>
            </div>
          ) : (
            <>
                <div className="-mt-1 font-poppins text-[12px] md:text-[16px] font-semibold text-[#031843] whitespace-nowrap">
                  {Number.isFinite(volume) ? formatUnidade(volume, unit) : '-'}
              </div>
              <div className="font-poppins text-[9px] md:text-[10px] text-[#031843] -mt-1 whitespace-nowrap">
                {volumeLabel}
              </div>
            </>
          )}
        </div>
        {showProjected && Number.isFinite(projectedVolume) && Number.isFinite(projAnimP) && (
          <div style={{ left: `${computeBallPct(projAnimP)}%`, transform: 'translateX(-50%)' }} className="absolute top-[76px] md:top-[80px] flex flex-col items-center z-[166] transition-none">
            <div className="relative grid place-items-center">
              <div
                className="absolute rounded-full border-2 opacity-40 animate-ping pointer-events-none md:hidden"
                style={{ width: '24px', height: '24px', borderColor: ballProjColor, zIndex: 160, animationDuration: '2.6s' }}
              />
              <div
                className="absolute rounded-full border-2 opacity-30 animate-ping pointer-events-none md:hidden"
                style={{ width: '24px', height: '24px', borderColor: ballProjColor, zIndex: 160, animationDelay: '1.3s', animationDuration: '2.6s' }}
              />
              <div
                className="absolute hidden md:block rounded-full border-4 opacity-40 animate-ping pointer-events-none"
                style={{ width: '35px', height: '35px', borderColor: ballProjColor, zIndex: 160, animationDuration: '2.6s' }}
              />
              <div
                className="absolute hidden md:block rounded-full border-4 opacity-30 animate-ping pointer-events-none"
                style={{ width: '35px', height: '35px', borderColor: ballProjColor, zIndex: 160, animationDelay: '1.3s', animationDuration: '2.6s' }}
              />
              <div
                className="relative mt-0.5 z-[170] w-6 h-6 md:w-[35px] md:h-[35px] rounded-full border-2 md:border-4 transition-colors duration-150 pointer-events-auto hover:ring-[8px] hover:ring-[#E9E9E9]/50"
                style={{ backgroundColor: ballProjColor, borderColor: ballProjColor }}
              />
            </div>
            <div className="-mt-1 w-[2px] h-4 transition-colors duration-150 pointer-events-none" style={{ backgroundColor: ballProjColor }} />
            {ballsClose && hasTwoBalls ? (
              <div className="relative mt-[-2px]" style={{ width: 0 }}>
                <div
                  style={{
                    position: 'absolute',
                    left: realIsLeft ? `${labelGapPx}px` : 'auto',
                    right: realIsLeft ? 'auto' : `${labelGapPx}px`,
                    width: `${labelWidthPx}px`,
                    textAlign: realIsLeft ? 'left' : 'right'
                  }}
                >
                  <div className="font-poppins text-[12px] md:text-[16px] font-semibold text-[#031843] whitespace-nowrap">
                    {Number.isFinite(projectedVolume) ? formatUnidade(projectedVolume, unit) : '-'}
                  </div>
                  <div className="font-poppins text-[9px] md:text-[10px] text-[#031843] -mt-1 whitespace-nowrap">
                    {projectedLabel}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="-mt-1 font-poppins text-[12px] md:text-[16px] font-semibold text-[#031843] whitespace-nowrap">
                  {Number.isFinite(projectedVolume) ? formatUnidade(projectedVolume, unit) : '-'}
                </div>
                <div className="font-poppins text-[9px] md:text-[10px] text-[#031843] -mt-1 whitespace-nowrap">
                  {projectedLabel}
                </div>
              </>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}